<template>
  <div
    :class="{
      'osk-products-filters-multilist': true,
      'osk-products-filters-multilist_withInfiniteScroll': needInfiniteScroll,
    }"
  >
    <div
      v-if="isTitle"
      class="osk-products-filters-multilist__title"
    >
      {{ filterInfo.name }}
    </div>

    <UikitFormInput
      v-if="isSearch"
      v-model="search"
      class="osk-products-filters-multilist__search"
      name="search"
      placeholder="Ищите по названию"
      :background="!isDesktop ? 'grey-4' : undefined"
      :icon-left="!isDesktop ? 'Search' : undefined"
      size="m"
    />

    <UikitList
      v-for="(value, index) in valuesComp"
      :key="index"
      :title="value.name !== filterInfo.name ? value.name : undefined"
      class="osk-products-filters-multilist__list"
    >
      <UikitListItem
        v-for="item in entries[index]"
        :key="item.id"
        no-padding
      >
        <UikitFormCheckbox
          v-model="multilist"
          name="multilist"
          :value="genItem(item)"
          round
          class="osk-products-filters-multilist__item"
          @update:model-value="handleCheckboxClick(item)"
        >
          <img
            v-if="item.image"
            :src="item.image"
            class="osk-products-filters-multilist__image"
            loading="lazy"
            :alt="item.value"
          >
          {{ item.value }}

          <UikitTooltip
            v-if="item.description"
            :title="item.value"
            @open="handleTooltipOpen(item)"
          >
            {{ item.description }}
          </UikitTooltip>
        </UikitFormCheckbox>
      </UikitListItem>
    </UikitList>
  </div>
</template>

<script lang="ts" setup>
import {
  MultiListFilter, ListValue, ListSectionEntry,
  ListSection,
} from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const props = defineProps<{
  filterInfo: MultiListFilter
  content?: HTMLElement
  page?: string
}>();

const emits = defineEmits<{
  (e: 'updateFilter', value?: ListValue[]): void,
}>();

const multilist = ref(genSelected());
const itemsCount = ref(40);
const search = ref('');

const { isDesktop } = useUiBreakpoints();

const isTitle = computed(() => (props.filterInfo.values?.length ?? 0) > 0);
const isSearch = computed(() => props.filterInfo.searchableSections.length > 0);

const entriesLength = computed(() => props.filterInfo.values?.reduce((result, value) => {
  // eslint-disable-next-line no-param-reassign
  result += value.entries.length ?? 0;
  return result;
}, 0) ?? 0);

const needInfiniteScroll = computed(() => entriesLength.value > 100);

const valuesComp = computed(() => {
  if (!isSearch.value || !search.value) return props.filterInfo.values;

  const newValue = props.filterInfo.searchableSections.reduce((result, section) => {
    const value = props.filterInfo.values?.find((val) => val.name === section);

    if (value) {
      result.entries.push(...value.entries);
    }

    return result;
  }, { name: '', entries: [] } as ListSection);

  return [newValue];
});

const entries = computed(() => valuesComp.value?.map((val, index) => {
  if (isSearch.value && search.value) {
    const result = val.entries.filter((entry) => entry.value.toLowerCase().includes(search.value.toLowerCase()));

    if (!needInfiniteScroll.value) return result;

    return result.slice(0, itemsCount.value);
  }

  if (!needInfiniteScroll.value) return val.entries;

  if (index === (valuesComp.value?.length ?? 0) - 1) {
    return val.entries.slice(0, itemsCount.value);
  }

  return val.entries;
}) ?? []);

function handleCheckboxClick(item: ListSectionEntry) {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: props.filterInfo.code,
    // eslint-disable-next-line no-param-reassign
    id_chapter: multilist.value.map((val) => val.id).reduce((result, id) => result += `${id};`, '').slice(0, -1),
    id_item: String(item.id),
    page: props.page,
  });
}

function handleTooltipOpen(item: ListSectionEntry) {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: item.value,
    page: props.page,
  });
}

function startInfiniteScroll() {
  useInfiniteScroll(
    props.content,
    () => {
      const { values } = props.filterInfo;

      if (!values) return;

      const lastEntriesLength = values[values.length - 1]?.entries.length;

      if (!lastEntriesLength || itemsCount.value >= lastEntriesLength) {
        props.content?.scrollBy(0, -16);
        return;
      }

      itemsCount.value += 40;

      // TODO after vueuse 10.* there is a bug
      props.content?.scrollBy(0, 1);
    },
    { distance: 10 },
  );
}

function genSelected() {
  return props.filterInfo.selectedValues ?? [];
}

function genItem({ id, value }: ListSectionEntry) {
  return { id, value };
}

onMounted(() => {
  if (props.content && needInfiniteScroll.value) {
    startInfiniteScroll();
  }
});

watch(multilist, (list) => {
  emits('updateFilter', list);
});

watch(() => props.filterInfo, () => {
  multilist.value = genSelected();

  if (multilist.value.length === 0) {
    search.value = '';
  }
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters-multilist {
  &_withInfiniteScroll {
    padding-bottom: 16px;
  }

  &__title {
    font-weight: 700;
    font-size: 22px;
    line-height: 27px;
    margin-bottom: 18px;

    @include media-query(lg-and-up) {
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      padding-bottom: 16px;
      margin-bottom: 0;
    }
  }

  &__search {
    margin-top: 2px;
    margin-bottom: 34px;

    @include media-query(lg-and-up) {
      margin-top: 9px;

      :deep(.osk-field__inner) {
        border-color: #000;
      }
    }
  }

  &__item {
    height: 52px;
    cursor: pointer;
    padding: 0 16px;
  }

  &__image {
    width: 32px;
    height: 32px;
    margin-right: $indent-medium;
  }

  :deep(.osk-checkbox__content) {
    width: 100%;

    .osk-tooltip {
      margin-left: auto;
      margin-right: 8px;
    }
  }

  :deep(.osk-list__title) {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);

    @include media-query(lg-and-up) {
      margin-bottom: 10px;
    }
  }
}
</style>
